// /day/:date — a day's frozen lineup: replay a past day (deterministic audio) or preview the
// lineup of a frozen upcoming day.
import { useEffect, useMemo, useState } from "react";
import { dateToDay, KITS, keyPairName, wizardLabel, type Lineup, type PeriodDef } from "@eb/shared";
import { clock } from "../stream";
import { api } from "../api";
import { engineHost } from "../audio/host";
import { Link, useTitle } from "../lib/router";
import { RiverPlayer, fetchServerTime } from "../river/player";
import { ServerSource } from "../river/serverSource";
import { formatClock } from "../river/slots";

const { slotStartMs } = clock;

const PAGE = 50;

export function Day({ date }: { date: string }) {
  useTitle(`Day ${date}`);
  const [epoch, setEpoch] = useState<number | null>(null);
  const [lineup, setLineup] = useState<Lineup | null>(null);
  const [period, setPeriod] = useState<PeriodDef | null>(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [playing, setPlaying] = useState<number | null>(null);
  const player = useMemo(() => (epoch !== null ? new RiverPlayer(new ServerSource(epoch)) : null), [epoch]);

  useEffect(() => {
    void (async () => {
      const { epochMs } = await fetchServerTime();
      setEpoch(epochMs);
      const day = dateToDay(date, epochMs);
      try {
        setPeriod(await api<PeriodDef>("GET", `/api/periods/${day}`));
        setLineup(await api<Lineup>("GET", `/api/lineups/${day}`));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => player?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  async function play(slot: number) {
    if (!player || !lineup || epoch === null) return;
    const host = await engineHost();
    player.stop();
    await player.syncClock();
    // Shift the song's clock to this slot's start.
    const realNow = player.worldNow() - player.shiftMs;
    player.shiftMs = slotStartMs(lineup.day, slot, epoch) - realNow;
    await player.join(host);
    setPlaying(slot);
  }

  const humans = lineup?.entries.filter((e) => e.kind === "human").length ?? 0;
  const rows = lineup?.entries.slice(page * PAGE, page * PAGE + PAGE) ?? [];
  return (
    <main id="main" className="page">
      <p>
        <Link href="/calendar">← Calendar</Link>
      </p>
      <h1>
        {new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </h1>
      {period && (
        <p>
          <strong>{keyPairName(period.key)}</strong> · {KITS[period.kit].name} · {period.genre}
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {lineup && (
        <>
          <p className="muted">
            {humans} loops by contributors, {lineup.entries.length - humans} Wizard fillers. Times are in your time
            zone.
          </p>
          <div className="row">
            {playing !== null && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  player?.stop();
                  setPlaying(null);
                }}
              >
                ■ Stop replay
              </button>
            )}
            <button type="button" className="btn btn-small" disabled={page === 0} onClick={() => setPage(page - 1)}>
              ← Earlier
            </button>
            <span className="small">
              Slots {page * PAGE + 1}–{Math.min(lineup.entries.length, page * PAGE + PAGE)} of {lineup.entries.length}
            </span>
            <button
              type="button"
              className="btn btn-small"
              disabled={(page + 1) * PAGE >= lineup.entries.length}
              onClick={() => setPage(page + 1)}
            >
              Later →
            </button>
          </div>
          <table className="day-list">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Song by</th>
                <th scope="col">Wizard</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.slot} className={playing === e.slot ? "now" : ""}>
                  <td>{epoch !== null && formatClock(slotStartMs(lineup.day, e.slot, epoch))}</td>
                  <td>
                    {e.kind === "human" && e.author ? (
                      <Link href={`/u/${encodeURIComponent(e.author)}`}>{e.author}</Link>
                    ) : (
                      (e.author ?? "former contributor")
                    )}
                  </td>
                  <td>{wizardLabel(e) ?? "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => void play(e.slot)}
                      aria-label={`Replay from slot ${e.slot + 1}`}
                    >
                      ▶ Play from here
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
